import { Button, Card, Grid, InputNumber, Select, Space } from "@arco-design/web-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Number, Currency } from "@/components/Numeric";
import { ColorfulTable } from "@/components/ColorfulTable";
import { PresetColorPicker } from "@/components/PresetColorPicker";
import Reference from "@/components/Reference";
import dayjs from "dayjs";
import { SOAPI } from "@/apis/sales";
import { UserProfile, UserProfileType } from "@/utils";
import { Link } from "react-router-dom";
import _ from "lodash";

const Row = Grid.Row;
const Col = Grid.Col;

const ColumnOptions = [
    "Item Name",
    "Description",
    "Open Qty",
    "Rate",
    "Open Amount",
    "SO#",
    "Reference#",
    "Customer",
    "SO Date",
    "Due Date",
    "Ready Date"
]

const OpenSOReportByItem = () => {
    const [totalOpenQantity, setTotalOpenQantity] = useState(0);
    const [totalOpenAmount, setTotalOpenAmount] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedColumnNames, setSelectedColumnNames] = useState(ColumnOptions);
    const [columns, setColumns] = useState([]);
    const [pagination, setPagination] = useState();
    const [data, setData] = useState([]);
    const [dueDateColor, setDueDateColor] = useState();
    const [dueDateDays, setDueDateDays] = useState();
    const [readyColor, setReadyColor] = useState();
    const [readyDays, setReadyDays] = useState();

    const originColumns = useMemo(() => [
        {
            title: "Item Name",
            dataIndex: "itemName",
        }, {
            title: "Description",
            dataIndex: "description",
        }, {
            title: "Open Qty",
            render: (col, record) => <Number value={record.quantity - record.fulfilledQty} />,
        }, {
            title: "Rate",
            render: (col, record) => <Currency value={record.rate} />
        }, {
            title: "Open Amount",
            render: (col, record) => <Currency value={record.rate * (record.quantity - record.fulfilledQty)} />
        }, {
            title: "SO#",
            dataIndex: "saleOrder.soNo",
            render: (col, record) => <Link to={`/workspace/sales/so/${record.soId}`}>{record.saleOrder.soNo}</Link>
        }, {
            title: "Reference#",
            render: (col, record) => <Reference value={record.saleOrder?.reference} />,
        }, {
            title: "Customer",
            dataIndex: "saleOrder.customerName",
        }, {
            title: "SO Date",
            dataIndex: "saleOrder.soDate",
        }, {
            title: <div>
                <span>Due Date</span>
                <div>
                    <InputNumber hideControl
                        placeholder="Days"
                        size="mini"
                        min={0}
                        style={{ width: 50 }}
                        value={dueDateDays}
                        onChange={(val) => setDueDateDays(val)} />
                    <PresetColorPicker
                        size="mini"
                        value={dueDateColor}
                        onChange={(color) => {
                            setDueDateColor(color);
                        }}
                    />
                </div>
            </div>,
            name: "Due Date",
            dataIndex: "saleOrder.dueDate",
            colorful: (record) => record.saleOrder.dueDate && dayjs(record.saleOrder.dueDate).isBefore(dayjs().add(dueDateDays ?? 0, 'day')),
            bgColor: dueDateColor,
            width: 110
        }, {
            title: <div>
                <span>Ready Date</span>
                <div>
                    <InputNumber hideControl
                        placeholder="Days"
                        size="mini"
                        min={0}
                        style={{ width: 50 }}
                        value={readyDays}
                        onChange={val => setReadyDays(val)} />
                    <PresetColorPicker
                        value={readyColor}
                        onChange={(color) => {
                            setReadyColor(color);
                        }}
                    />
                </div>
            </div>,
            name: "Ready Date",
            dataIndex: "saleOrder.readyDate",
            colorful: (record) => record.saleOrder.readyDate && dayjs(record.saleOrder.readyDate).isBefore(dayjs().add(readyDays ?? 0, 'day')),
            bgColor: readyColor,
            width: 110
        }
    ], [readyColor, readyDays, dueDateColor, dueDateDays]);

    const loadColumns = useCallback(() => {
        const cols = originColumns.filter(col => selectedColumnNames.includes(col.title) || selectedColumnNames.includes(col.name));
        setColumns(cols);
    }, [originColumns, selectedColumnNames]);

    const getData = useCallback(async (page, customerIds, itemIds) => {
        const value = await SOAPI.getItemReport({
            page: page ?? 1,
            customerIds: customerIds,
            itemIds: itemIds,
        })

        const { totalQuantity, totalAmount, items } = value;
        setTotalOpenQantity(totalQuantity);
        setTotalOpenAmount(totalAmount);
        setData(items);

        setPagination((pagination) => ({
            ...pagination,
            current: 1,
            total: items.length,
        }));
    }, []);

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.OPEN_SO_REPORT_BY_ITEM, {
            columns: selectedColumnNames,
            dueDateColor,
            dueDateDays,
            readyColor,
            readyDays,
        });
    }

    useEffect(() => {
        const getOpenSOCustomersAndItems = async () => {
            const customers = await SOAPI.openCustomers();
            const sortedCustomers = _.sortBy(customers, "customerName");
            setCustomers(sortedCustomers);
            const items = await SOAPI.openItems();
            const sortedItems = _.sortBy(items, "itemName");
            setItems(sortedItems);
        }

        getOpenSOCustomersAndItems();
    }, [])


    useEffect(() => {
        loadColumns();
    }, [loadColumns]);

    useEffect(() => {
        const load = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.OPEN_SO_REPORT_BY_ITEM);
            if (profile) {
                profile.columns && setSelectedColumnNames(profile.columns);
                profile.dueDateColor && setDueDateColor(profile.dueDateColor);
                profile.dueDateDays && setDueDateDays(profile.dueDateDays);
                profile.readyColor && setReadyColor(profile.readyColor);
                profile.readyDays && setReadyDays(profile.readyDays);
            }
        };
        load();
    }, []);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <Card title="Open SO Report by Item" extra={<Space>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select customers..."
                style={{ width: 200 }}
                value={selectedCustomers}
                maxTagCount={1}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setSelectedCustomers(value);
                }}
                options={customers.map(item => ({
                    label: item.customerName,
                    value: item.id,
                }))}
            />
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select items..."
                maxTagCount={1}
                style={{ width: 200 }}
                value={selectedItems}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setSelectedItems(value);
                }}
                options={items.map(item => ({
                    label: item.itemName,
                    value: item.id
                }))}
            />
            <Button type="primary" onClick={() => getData(1, selectedCustomers, selectedItems)}>Search</Button>
            <Button type="primary" onClick={() => saveProfile()}>Save Profile</Button>
        </Space>}>
            <Row>
                <Col flex='auto'>
                    <Space>
                        <span>Total Open Qty: <Number value={totalOpenQantity} /></span>
                        <span>Total Open Amount: <Currency value={totalOpenAmount} /></span>
                    </Space>
                </Col>
                <Col flex='100px'>
                    <Select
                        mode="multiple"
                        bordered={false}
                        placeholder="Columns"
                        triggerProps={{
                            autoAlignPopupWidth: false,
                            autoAlignPopupMinWidth: true,
                        }}
                        value={selectedColumnNames}
                        renderTag={(props, index) => {
                            return index === 0 ? <span style={{ margin: "0 5px" }}>Columns</span> : null;
                        }}
                        onChange={(selected) => {
                            setSelectedColumnNames(selected);
                        }}
                        options={ColumnOptions}
                    />
                </Col>
            </Row>
            <ColorfulTable rowKey="id"
                columns={columns}
                pagination={pagination}
                data={data}
                onChange={(pagination) => {
                    setPagination(pagination);
                }}
            />
        </Card>
    )
}

export default OpenSOReportByItem;